/**
 * CR283: Merchant Aliases Card Component
 */

import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Trash2, Loader2 } from "lucide-react";
import type { MerchantAlias } from "@/hooks/useMerchantsDirectory";
import { useMerchantDirectoryMutations } from "@/hooks/useMerchantsDirectory";

const ALIAS_TYPES = [
  { value: "manual", label: "Manual" },
  { value: "filename", label: "Filename" },
  { value: "domain", label: "Domain" },
  { value: "ocr", label: "OCR" },
  { value: "display", label: "Display" },
];

interface MerchantAliasesCardProps {
  merchantKey: string;
  aliases: MerchantAlias[];
  loading: boolean;
}

export function MerchantAliasesCard({ merchantKey, aliases, loading }: MerchantAliasesCardProps) {
  const [newAlias, setNewAlias] = useState("");
  const [newType, setNewType] = useState("manual");
  const [newPriority, setNewPriority] = useState(0);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { addAlias, isAddingAlias, deleteAlias, isDeletingAlias } = useMerchantDirectoryMutations();

  const handleAdd = async () => {
    if (!newAlias.trim()) return;
    await addAlias({
      merchantKey,
      alias: newAlias.trim(),
      aliasType: newType,
      priority: newPriority,
    });
    setNewAlias("");
    setNewPriority(0);
  };

  const handleDelete = async (aliasId: string) => {
    if (!confirm("Delete this alias?")) return;
    setDeletingId(aliasId);
    try {
      await deleteAlias({ aliasId, merchantKey });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Aliases</CardTitle>
        <CardDescription>
          Alternative names for filename matching and OCR recognition.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Add Alias Form */}
        <div className="flex items-end gap-2">
          <div className="flex-1 space-y-1">
            <Input
              value={newAlias}
              onChange={(e) => setNewAlias(e.target.value)}
              placeholder="New alias..."
            />
          </div>
          <div className="w-28">
            <Select value={newType} onValueChange={setNewType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ALIAS_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-20">
            <Input
              type="number"
              min={0}
              max={100}
              value={newPriority}
              onChange={(e) => setNewPriority(parseInt(e.target.value) || 0)}
              placeholder="Priority"
            />
          </div>
          <Button onClick={handleAdd} disabled={!newAlias.trim() || isAddingAlias}>
            {isAddingAlias ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
          </Button>
        </div>

        {/* Aliases Table */}
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : aliases.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            No aliases configured for this merchant.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Alias</TableHead>
                <TableHead>Normalized</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="w-16">Priority</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {aliases.map((alias) => (
                <TableRow key={alias.id}>
                  <TableCell className="font-medium">{alias.alias}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {alias.alias_normalized}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{alias.alias_type}</Badge>
                  </TableCell>
                  <TableCell className="tabular-nums text-center">{alias.priority}</TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handleDelete(alias.id)}
                      disabled={isDeletingAlias && deletingId === alias.id}
                    >
                      {isDeletingAlias && deletingId === alias.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4 text-destructive" />
                      )}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
